(() => {
  if (window.AlawooderAbout) {
    window.AlawooderAbout.mountAll(document);
    return;
  }

  const instances = new Map();
  const pad = value => String(value).padStart(2, '0');

  function mount(section) {
    if (instances.has(section)) return;
    const cleanups = [];

    section.querySelectorAll('[data-about-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-about-track]');
      const controls = carousel.querySelector('[data-about-controls]');
      const cards = Array.from(track?.querySelectorAll('[data-about-card]') || []);
      const previous = controls?.querySelector('[data-about-prev]');
      const next = controls?.querySelector('[data-about-next]');
      const count = controls?.querySelector('[data-about-count]');
      if (!track || !controls || !cards.length || !previous || !next || !count) return;

      const mobile = window.matchMedia('(max-width: 760px)');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let frame = 0;
      let index = 0;
      let pending = null;
      let trackWidth = track.clientWidth;

      const position = card => card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      const clampedPosition = card => Math.max(0, Math.min(position(card), track.scrollWidth - track.clientWidth));
      const render = () => {
        controls.hidden = !mobile.matches || cards.length < 2 || track.scrollWidth <= track.clientWidth + 2;
        if (!mobile.matches) { pending = null; return; }
        if (pending && Math.abs(track.scrollLeft - pending.left) < 3) pending = null;
        const offset = track.scrollLeft;
        index = cards.reduce((nearest, card, candidate) => Math.abs(position(card) - offset) < Math.abs(position(cards[nearest]) - offset) ? candidate : nearest, 0);
        if (offset + track.clientWidth >= track.scrollWidth - 2) index = cards.length - 1;
        const shown = pending?.index ?? index;
        count.textContent = `${pad(shown + 1)} / ${pad(cards.length)}`;
        previous.disabled = shown === 0;
        next.disabled = shown === cards.length - 1;
      };
      const queueRender = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          render();
        });
      };
      const interrupt = () => {
        if (!pending) return;
        pending = null;
        track.scrollTo({ left: track.scrollLeft, behavior: 'instant' });
        render();
      };
      const settle = () => {
        if (pending) {
          const destination = pending;
          pending = null;
          track.scrollTo({ left: destination.left, behavior: 'instant' });
        }
        render();
      };
      const navigate = (target, forceImmediate = false) => {
        if (!mobile.matches) return;
        window.cancelAnimationFrame(frame);
        frame = 0;
        const destination = Math.max(0, Math.min(target, cards.length - 1));
        const left = clampedPosition(cards[destination]);
        const immediate = forceImmediate || reducedMotion.matches;
        pending = !immediate && Math.abs(track.scrollLeft - left) >= 3 ? { index: destination, left } : null;
        index = destination;
        track.scrollTo({ left, behavior: immediate ? 'instant' : 'smooth' });
        render();
      };
      const goPrevious = () => navigate((pending?.index ?? index) - 1);
      const goNext = () => navigate((pending?.index ?? index) + 1);
      const onKey = event => {
        if (!mobile.matches || event.target !== track) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); goPrevious(); }
        if (event.key === 'ArrowRight') { event.preventDefault(); goNext(); }
        if (event.key === 'Home') { event.preventDefault(); navigate(0); }
        if (event.key === 'End') { event.preventDefault(); navigate(cards.length - 1); }
      };
      const onMedia = () => {
        pending = null;
        track.scrollTo({ left: mobile.matches ? track.scrollLeft : 0, behavior: 'instant' });
        render();
      };
      const onBlockSelect = event => navigate(event.detail.index, true);
      previous.addEventListener('click', goPrevious);
      next.addEventListener('click', goNext);
      track.addEventListener('scroll', queueRender, { passive: true });
      track.addEventListener('scrollend', settle);
      track.addEventListener('pointerdown', interrupt, { passive: true });
      track.addEventListener('wheel', interrupt, { passive: true });
      track.addEventListener('keydown', onKey);
      track.addEventListener('aw:about:select', onBlockSelect);
      mobile.addEventListener('change', onMedia);
      const resize = new ResizeObserver(() => {
        if (track.clientWidth !== trackWidth) {
          trackWidth = track.clientWidth;
          interrupt();
        }
        queueRender();
      });
      resize.observe(track);
      render();
      cleanups.push(() => {
        window.cancelAnimationFrame(frame);
        resize.disconnect();
        previous.removeEventListener('click', goPrevious);
        next.removeEventListener('click', goNext);
        track.removeEventListener('scroll', queueRender);
        track.removeEventListener('scrollend', settle);
        track.removeEventListener('pointerdown', interrupt);
        track.removeEventListener('wheel', interrupt);
        track.removeEventListener('keydown', onKey);
        track.removeEventListener('aw:about:select', onBlockSelect);
        mobile.removeEventListener('change', onMedia);
      });
    });
    instances.set(section, () => cleanups.forEach(cleanup => cleanup()));
  }

  function mountAll(scope = document) {
    if (scope.matches?.('[data-about-section]')) mount(scope);
    scope.querySelectorAll('[data-about-section]').forEach(mount);
  }

  window.AlawooderAbout = { mountAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  else mountAll();
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((cleanup, section) => {
      if (event.target === section || event.target.contains(section)) {
        cleanup();
        instances.delete(section);
      }
    });
  });
  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest('[data-about-card]');
    const track = card?.closest('[data-about-track]');
    if (!card || !track || !window.matchMedia('(max-width: 760px)').matches) return;
    const index = Array.from(track.querySelectorAll('[data-about-card]')).indexOf(card);
    track.dispatchEvent(new CustomEvent('aw:about:select', { detail: { index } }));
  });
})();
